import { Head, useForm } from '@inertiajs/react'
import { FormEvent } from 'react'
import SuperAdminLayout from '../_SuperAdminLayout'
import { sortPeople } from '../../../utils/peopleSort'

interface SuperAdmin {
  id: number
  email: string
  full_name: string
  is_self: boolean
  last_sign_in_at_display: string
  created_at_display: string
}

interface Props {
  super_admins: SuperAdmin[]
  errors: Record<string, string[] | string>
}

export default function AdminsIndex({ super_admins, errors }: Props) {
  const form = useForm({
    super_admin: { email: '', first_name: '', last_name: '', password: '' },
  })

  const fieldError = (key: string): string | undefined => {
    const e = (errors as Record<string, string[] | string>)?.[key]
    if (!e) return undefined
    return Array.isArray(e) ? e.join(', ') : e
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    form.post('/super_admin/super_admins', {
      preserveScroll: true,
      onSuccess: () => form.reset(),
    })
  }

  const handleRemove = (sa: SuperAdmin) => {
    if (!confirm(`Remove SuperAdmin access for ${sa.email}? They will no longer be able to sign in to the admin panel.`)) return
    form.delete(`/super_admin/super_admins/${sa.id}`, { preserveScroll: true })
  }

  return (
    <SuperAdminLayout>
      <Head title="SuperAdmins" />
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text-primary">SuperAdmins</h1>
        <p className="text-sm text-text-muted mt-1">
          People who can sign in to this admin panel and manage every tenant.
        </p>
      </div>

      {fieldError('base') && (
        <div className="mb-4 px-4 py-3 bg-[#ED1C24]/10 text-[#ED1C24] rounded-lg text-sm">
          {fieldError('base')}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-stone/20 rounded-lg overflow-hidden">
          {super_admins.length === 0 ? (
            <div className="py-10 text-center text-text-muted text-sm">No SuperAdmins yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#F5F5F5] text-text-secondary">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">Name</th>
                  <th className="text-left px-4 py-2.5 font-medium">Email</th>
                  <th className="text-left px-4 py-2.5 font-medium">Last sign in</th>
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {sortPeople(super_admins).map(sa => (
                  <tr key={sa.id} className="border-t border-stone/15">
                    <td className="px-4 py-3 text-text-primary font-medium">
                      {sa.full_name}
                      {sa.is_self && (
                        <span className="ml-2 text-xs bg-charcoal/15 text-text-primary px-1.5 py-0.5 rounded-full">you</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-text-muted">{sa.email}</td>
                    <td className="px-4 py-3 text-text-muted">{sa.last_sign_in_at_display}</td>
                    <td className="px-4 py-3 text-right">
                      {!sa.is_self && (
                        <button
                          type="button"
                          onClick={() => handleRemove(sa)}
                          className="text-xs text-[#ED1C24] hover:underline font-medium"
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="bg-white border border-stone/20 rounded-lg p-5 h-fit">
          <h2 className="text-base font-semibold text-text-primary mb-4">Add SuperAdmin</h2>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">Email</label>
              <input
                type="email"
                value={form.data.super_admin.email}
                onChange={e => form.setData('super_admin', { ...form.data.super_admin, email: e.target.value })}
                className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#351E1D]/20"
                required
              />
              {fieldError('email') && <p className="text-xs text-[#ED1C24] mt-1">{fieldError('email')}</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">First name</label>
                <input
                  type="text"
                  value={form.data.super_admin.first_name}
                  onChange={e => form.setData('super_admin', { ...form.data.super_admin, first_name: e.target.value })}
                  className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#351E1D]/20"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Last name</label>
                <input
                  type="text"
                  value={form.data.super_admin.last_name}
                  onChange={e => form.setData('super_admin', { ...form.data.super_admin, last_name: e.target.value })}
                  className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#351E1D]/20"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-1">Password</label>
              <input
                type="password"
                value={form.data.super_admin.password}
                onChange={e => form.setData('super_admin', { ...form.data.super_admin, password: e.target.value })}
                className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#351E1D]/20"
                required
              />
              {fieldError('password') && <p className="text-xs text-[#ED1C24] mt-1">{fieldError('password')}</p>}
              <p className="text-xs text-text-muted mt-1">At least 6 characters.</p>
            </div>
            <button
              type="submit"
              disabled={form.processing}
              className="w-full px-4 py-2 bg-[#351E1D] text-white text-sm font-semibold rounded-lg hover:bg-[#351E1D]/90 disabled:opacity-60"
            >
              {form.processing ? 'Adding…' : 'Add SuperAdmin'}
            </button>
          </form>
        </div>
      </div>
    </SuperAdminLayout>
  )
}
